import AddressAutocomplete, { ISelectedAddressRecord } from "@mirage/components/addressBox/AddressAutocomplete";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { Box, Grid, TextField, Typography } from "@mui/material";
import { PPConstants } from "@shared/publicportal/constants";
import { PropertySearchFilter } from "@shared/publicportal/contracts/PropertySearchFilter";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import React, { memo } from "react";

interface PropertySearchFieldsProps {
    state: PropertySearchFilter;
    setStateModifier: (modifier: Partial<PropertySearchFilter>) => void;
}

const PropertySearchFields = ({ state, setStateModifier }: PropertySearchFieldsProps) => {
    const { isMobile } = useDeviceDetect();
    const handleGazetteerOnSelect = (props: ISelectedAddressRecord) => {
        setStateModifier({
            address: props.address,
            addressLine1: props.addressLines.line1,
            addressLine2: props.addressLines.line2,
            addressLine3: props.addressLines.line3,
            postTown: props.addressDetails.TOWN_NAME,
            postCode: props.addressDetails.POSTCODE,
            uprn: props.addressDetails.UPRN,
            easting: props.point?.easting,
            northing: props.point?.northing,
        });
    };
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;

    return (
        <Grid container className="search-container propertySearch">
            <Grid container size={{ xs: 12 }}>
                <Grid size={{ xs: 12 }}>
                    <AddressAutocomplete
                        localGazetteerUrl={clientSettings?.LOCAL_GAZETTEER_URL as string}
                        localGazetteerToken={clientSettings?.LOCAL_GAZETTEER_TOKEN as string}
                        nationalGazetteerUrl={clientSettings?.NATIONAL_GAZETTEER_URL as string}
                        nationalGazetteerToken={clientSettings?.NATIONAL_GAZETTEER_TOKEN as string}
                        size="small"
                        gazetteer={"national"}
                        onSelect={handleGazetteerOnSelect}
                        gazetteerChoice
                    />
                </Grid>
            </Grid>
            {isMobile ? <PropertySearchMobileFields state={state} /> : <PropertySearchDesktopFields setStateModifier={setStateModifier} state={state} />}
        </Grid>
    );
};

const PropertySearchMobileFields = memo(({ state }: Pick<PropertySearchFieldsProps, "state">) => {
    const eastingNorthingFilled = state.easting && state.northing;
    const anyValueProvided =
        state.address || state.easting || state.northing || state.addressLine1 || state.addressLine2 || state.addressLine3 || state.postTown || state.postCode || state.uprn;

    if (!anyValueProvided) {
        return null;
    }

    return (
        <Grid container>
            <Grid size={{ xs: 12 }}>
                <Box textAlign="left">
                    <Typography sx={{ fontWeight: "600" }}>Address </Typography>
                    <Box marginLeft="5px">
                        <Typography variant="body1">{state.addressLine1}</Typography>
                        <Typography variant="body1">{state.addressLine2}</Typography>
                        <Typography variant="body1">{state.addressLine3}</Typography>
                        <Typography variant="body1">{state.postTown}</Typography>
                        <Typography variant="body1">{state.postCode}</Typography>
                    </Box>
                </Box>
                <Box textAlign="left">
                    <Typography sx={{ fontWeight: "600" }}>Easting / Northing </Typography>
                    <Box marginLeft="5px">
                        <Typography variant="body1">{eastingNorthingFilled ? `${state.easting} / ${state.northing}` : ""}</Typography>
                    </Box>
                </Box>
                <Box textAlign="left">
                    <Typography sx={{ fontWeight: "600" }}>UPRN </Typography>
                    <Box marginLeft="5px">
                        <Typography variant="body1">{state.uprn}</Typography>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
});

const PropertySearchDesktopFields = memo(({ state, setStateModifier }: PropertySearchFieldsProps) => {
    return (
        <Grid container columnGap={3}>
            <Grid container size={{ xs: 4 }}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="address"
                        value={state.address || ""}
                        onChange={(event) => setStateModifier({ address: event.target.value })}
                        key="propertySearch-address-key"
                        size="small"
                        id="propertySearch-address"
                        label="Address"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                        multiline
                        rows={5}
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="easting"
                        value={state.easting || ""}
                        onChange={(event) => setStateModifier({ easting: event.target.value })}
                        key="propertySearch-easting-key"
                        size="small"
                        id="propertySearch-easting"
                        label="Easting"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="northing"
                        value={state.northing || ""}
                        onChange={(event) => setStateModifier({ northing: event.target.value })}
                        key="propertySearch-northing-key"
                        size="small"
                        id="propertySearch-northing"
                        label="Northing"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
            </Grid>
            <Grid container size={{ xs: 4 }} rowGap={3}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="addressLine1"
                        value={state.addressLine1 || ""}
                        onChange={(event) => setStateModifier({ addressLine1: event.target.value })}
                        key="propertySearch-addressLine1-key"
                        size="small"
                        id="propertySearch-addressLine1"
                        label="Address Line 1"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="addressLine2"
                        value={state.addressLine2 || ""}
                        onChange={(event) => setStateModifier({ addressLine2: event.target.value })}
                        key="propertySearch-addressLine2-key"
                        size="small"
                        id="propertySearch-addressLine2"
                        label="Address Line 2"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="addressLine3"
                        value={state.addressLine3 || ""}
                        onChange={(event) => setStateModifier({ addressLine3: event.target.value })}
                        key="propertySearch-addressLine3-key"
                        size="small"
                        id="propertySearch-addressLine3"
                        label="Address Line 3"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="postTown"
                        value={state.postTown || ""}
                        onChange={(event) => setStateModifier({ postTown: event.target.value })}
                        key="propertySearch-postTown-key"
                        size="small"
                        id="propertySearch-postTown"
                        label="Post Town"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="postCode"
                        value={state.postCode || ""}
                        onChange={(event) => setStateModifier({ postCode: event.target.value })}
                        key="propertySearch-postCode-key"
                        size="small"
                        id="propertySearch-postCode"
                        label="Post Code"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        disabled
                        name="uprn"
                        value={state.uprn || ""}
                        onChange={(event) => setStateModifier({ uprn: event.target.value })}
                        key="propertySearch-uprn-key"
                        size="small"
                        id="propertySearch-uprn"
                        label="UPRN"
                        variant="outlined"
                        className="min-width-18rem propertySearch__field"
                    />
                </Grid>
            </Grid>
        </Grid>
    );
});

export default PropertySearchFields;
